import Box from '@/common/components/Box';
import Select from '@/common/components/Select';
import { SearchIcon } from 'lucide-react';
import { useParams } from 'react-router-dom';
import { ControlledByType, GovernmentAllocationAllowedCategoriesAndProducts, GovernmentAllocationAllowedCategoryAndProductData, GovernmentAllocationDistributionsLimitsData } from '@/features/privateCompanies/types/companyGovernmentAllocation.type';
import { useMemo, useState } from 'react';
import { useGovernmentAllocationsDistributionsLimits } from '@/features/privateCompanies/hooks/governmentAllocations/distributionsLimits/useGovernmentAllocationsDistributionsLimits';
import { useGovernmentAllocationsAllowedCategoriesAndProducts } from '@/features/privateCompanies/hooks/useGovernmentAllocationsAllowedCategoriesAndProducts';
import { GovernmentAllocationsAllowedCategoriesAndProductsTransformer } from '@/features/privateCompanies/services/governmentAllocationAllowedCategoriesAndProductsTransformer';
import CreateDistributionLimit from './GovernmentAllocationDistributionsLimits/CreateDistributionLimit';
import { Option } from '@/common/types/option';
import ProductCategoryDistributionLimitItem from './GovernmentAllocationDistributionsLimits/ProductCategoryDistributionLimitItem';
import Skeleton from '@/common/components/Skeleton';
import { EmptyDataMessage } from '@/common/components/EmptyDataMessage';
import useUserPermissions from '@/common/hooks/useUserPermissions';
import { Tooltip } from 'react-tooltip';

interface GovernmentAllocationDistributionsLimitsProps {
  controlledBy?: ControlledByType;
}

export const GovernmentAllocationDistributionsLimits = ({ controlledBy = 'currency' }: GovernmentAllocationDistributionsLimitsProps) => {
  const { id } = useParams();

  const [selectedProductCategory, setSelectedProducCategory] = useState<(GovernmentAllocationAllowedCategoryAndProductData & Option) | null>(null);

  const {
    data: allowedCategoriesAndProducts,
    isPending: isAllowedCategoriesAndProductsPending,
  } = useGovernmentAllocationsAllowedCategoriesAndProducts<GovernmentAllocationAllowedCategoriesAndProducts>({
    governmentAllocationId: id,
  });

  const allowedCategoriesOptions = useMemo(() => {
    return isAllowedCategoriesAndProductsPending
      ? []
      : GovernmentAllocationsAllowedCategoriesAndProductsTransformer.optionsTransformer(
        'categories', controlledBy, true
      )(allowedCategoriesAndProducts || {})
  }, [isAllowedCategoriesAndProductsPending]);

  const {
    data: distributionsLimits,
    isPending: distributionsLimitsIsPending,
    dataUpdatedAt: distributionsLimitsUpdatedAt,
  } = useGovernmentAllocationsDistributionsLimits<GovernmentAllocationDistributionsLimitsData>({
    governmentAllocationId: id,
  });

  const hasProductCategoryDistributionLimits = useMemo(() => {
    return Number(distributionsLimits?.product_category_distribution_limits?.length) > 0;
  }, [distributionsLimitsUpdatedAt]);

  const { hasUserPermission } = useUserPermissions();

  const hasUserPermissionToUpdateDistributionsLimits = hasUserPermission('Admin::Billing::GovernmentAllocations::DistributionsLimits');

  return (
    <>
      <Box className="flex flex-col gap-4">
        <p>
          Limites de distribuição do empenho
        </p>

        <div data-tooltip-id={!hasUserPermissionToUpdateDistributionsLimits ? 'tooltip-user-has-no-permission' : ''}>
          <Select
            placeholder={
              <div className="flex items-center gap-2">
                <SearchIcon strokeWidth={1.5} />

                <p>
                  Pesquisar categorias
                </p>
              </div>
            }
            options={allowedCategoriesOptions.filter((categoryOption) => {
              return !distributionsLimits?.product_category_distribution_limits?.some(
                ({ product_category }) => product_category?.id === categoryOption.value
              );
            }) || []}
            value={''}
            onChange={(newValue) => setSelectedProducCategory(newValue as GovernmentAllocationAllowedCategoryAndProductData & Option)}
            isDisabled={!hasUserPermissionToUpdateDistributionsLimits || isAllowedCategoriesAndProductsPending}
          />
        </div>

        <Tooltip id="tooltip-user-has-no-permission" place="bottom">
          Você não tem permissão
        </Tooltip>

        <div
          className="flex max-h-[calc(100vh-610px)] flex-col gap-4 overflow-y-auto"
        >
          {distributionsLimitsIsPending && (
            <>
              <Box className='p-4 flex flex-col gap-2.5'>
                <div>
                  <Skeleton className='h-7 w-32 bg-white-100' />
                </div>

                <div className='flex flex-col gap-1'>
                  <Skeleton className='h-4 w-24' />

                  <Skeleton className='h-6 w-36' />
                </div>

                <Box className='p-3 flex flex-col gap-2.5'>
                  <Skeleton className='h-7 w-32 bg-white-100' />

                  <div className='flex flex-col gap-1'>
                    <Skeleton className='h-4 w-24' />

                    <Skeleton className='h-6 w-36' />
                  </div>
                </Box>
              </Box>
            </>
          )}

          {!distributionsLimitsIsPending && hasProductCategoryDistributionLimits && distributionsLimits?.product_category_distribution_limits?.map((productCategoryDistributionLimit) => (
            <ProductCategoryDistributionLimitItem
              key={productCategoryDistributionLimit.id}
              governmentAllocationId={id}
              productCategoryDistributionLimit={productCategoryDistributionLimit}
              controlledBy={controlledBy}
            />
          ))}

          {!distributionsLimitsIsPending && !hasProductCategoryDistributionLimits && (
            <EmptyDataMessage />
          )}
        </div>
      </Box>

      <CreateDistributionLimit
        productCategory={selectedProductCategory}
        onDismiss={() => setSelectedProducCategory(null)}
        governmentAllocationId={id}
        controlledBy={controlledBy}
      />
    </>
  );
};
